import type {
  BriefView,
  MeetingDigest,
  ConnectionView,
  ConnectorCatalog,
  CreatedInvite,
  InvitePreview,
  MeetingInput,
  MeetingView,
  InviteSendResult,
  ImportProjectsInput,
  ImportProjectsResult,
  JiraDraftView,
  JiraProjectInfo,
  OrgDetail,
  OrgRole,
  OrgSummary,
  ScheduleView,
  SendAllResult,
  SetEmailsResult,
  TeamBoardView,
  TeamRole,
  TeamRosterView,
  TeamView,
  VerticalId,
} from '@live-stream/shared'
import { ApiError, type HttpClient } from '../http.js'
import { readKey, writeKey, type KeyValueStore } from '../storage.js'
import type { OrgEvents, OrgListener } from './org-events.js'

const CURRENT_KEY = 'ls.org'

const enc = encodeURIComponent

/**
 * The company route: organisations, their people, teams and invites, the marketplace of tools an
 * organisation can connect, and Jira. Who may do what is `orgCan` (exported with the shared
 * rules); the server enforces it, and a person outside an organisation is told it does not exist.
 */
export class Orgs {
  constructor(
    private readonly http: HttpClient,
    private readonly storage: KeyValueStore | null,
    private readonly events: OrgEvents,
  ) {}

  /** The signed-in person's organisations. */
  async list(): Promise<OrgSummary[]> {
    return (await this.http.get<{ orgs: OrgSummary[] }>('/orgs')).orgs
  }

  /** Start an organisation; the caller owns it. Its vertical decides which connectors it is offered. */
  async create(name: string, vertical: VerticalId): Promise<OrgSummary> {
    return (await this.http.post<{ org: OrgSummary }>('/orgs', { name, vertical })).org
  }

  /** One organisation: members, teams, and (for admins) the open invites. */
  async get(orgId: string): Promise<OrgDetail> {
    return (await this.http.get<{ org: OrgDetail }>(`/orgs/${enc(orgId)}`)).org
  }

  /** Admins only. Changes which connectors the organisation is offered; nothing already connected is affected. */
  async setVertical(orgId: string, vertical: VerticalId): Promise<void> {
    await this.http.put(`/orgs/${enc(orgId)}/vertical`, { vertical })
  }

  async rename(orgId: string, name: string): Promise<void> {
    await this.http.patch(`/orgs/${enc(orgId)}`, { name })
  }

  /** Owners only. Takes its teams, invites and connections with it. */
  async remove(orgId: string): Promise<void> {
    await this.http.delete(`/orgs/${enc(orgId)}`)
  }

  async setRole(orgId: string, userId: string, role: OrgRole): Promise<void> {
    await this.http.patch(`/orgs/${enc(orgId)}/members/${enc(userId)}`, { role })
  }

  /** Remove someone; for your own id, leave. They leave the organisation's teams too. */
  async removeMember(orgId: string, userId: string): Promise<void> {
    await this.http.delete(`/orgs/${enc(orgId)}/members/${enc(userId)}`)
  }

  /** A link to join. The token comes back once; only its hash is kept. */
  async invite(orgId: string, input: { role?: OrgRole; email?: string } = {}): Promise<CreatedInvite> {
    return this.http.post(`/orgs/${enc(orgId)}/invites`, input)
  }

  async cancelInvite(orgId: string, inviteId: string): Promise<void> {
    await this.http.delete(`/orgs/${enc(orgId)}/invites/${enc(inviteId)}`)
  }

  /** What an invite link is for, before anyone accepts. Works signed out. */
  async previewInvite(token: string): Promise<InvitePreview> {
    return (await this.http.get<{ invite: InvitePreview }>(`/orgs/invites/${enc(token)}`)).invite
  }

  /**
   * Finish the account an emailed invite is for: choose a password and the account is made, joined and
   * signed in on the server (the session cookie is set). Call `auth.restore()` after, so this client
   * adopts the session. Returns the organisation joined.
   */
  async activateInvite(token: string, input: { password: string; displayName?: string }): Promise<string> {
    return (await this.http.post<{ orgId: string }>(`/orgs/invites/${enc(token)}/activate`, input)).orgId
  }

  /** Join through an invite link. Returns the organisation joined. */
  async acceptInvite(token: string): Promise<string> {
    return (await this.http.post<{ orgId: string }>(`/orgs/invites/${enc(token)}/accept`)).orgId
  }

  async createTeam(orgId: string, name: string): Promise<TeamView> {
    return (await this.http.post<{ team: TeamView }>(`/orgs/${enc(orgId)}/teams`, { name })).team
  }

  async renameTeam(orgId: string, teamId: string, name: string): Promise<void> {
    await this.http.patch(`/orgs/${enc(orgId)}/teams/${enc(teamId)}`, { name })
  }

  async deleteTeam(orgId: string, teamId: string): Promise<void> {
    await this.http.delete(`/orgs/${enc(orgId)}/teams/${enc(teamId)}`)
  }

  /** Put someone in a team, or change their role in it (a lead is the team's project manager). */
  async setTeamMember(
    orgId: string,
    teamId: string,
    userId: string,
    role: TeamRole = 'member',
  ): Promise<void> {
    await this.http.put(`/orgs/${enc(orgId)}/teams/${enc(teamId)}/members/${enc(userId)}`, { role })
  }

  async removeTeamMember(orgId: string, teamId: string, userId: string): Promise<void> {
    await this.http.delete(`/orgs/${enc(orgId)}/teams/${enc(teamId)}/members/${enc(userId)}`)
  }

  /** The tools this organisation can connect, in sections, and which are connected. */
  async connectors(orgId: string): Promise<ConnectorCatalog> {
    return (await this.http.get<{ catalog: ConnectorCatalog }>(`/orgs/${enc(orgId)}/connectors`))
      .catalog
  }

  // ------------------------------------------------------------------ Jira

  /** Where to send the browser to authorise the app on Atlassian (admins). It returns to the Jira connector page. */
  async connectJira(orgId: string): Promise<string> {
    return (await this.http.post<{ url: string }>(`/orgs/${enc(orgId)}/connectors/jira/connect`)).url
  }

  /** The organisation's Jira connection, or null when it has none. */
  async jira(orgId: string): Promise<ConnectionView | null> {
    try {
      return (
        await this.http.get<{ connection: ConnectionView }>(`/orgs/${enc(orgId)}/connectors/jira`)
      ).connection
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null
      throw err
    }
  }

  /** The sites to choose from after consent, when the person authorised more than one. */
  async jiraDraft(orgId: string, draftId: string): Promise<JiraDraftView> {
    return (
      await this.http.get<{ draft: JiraDraftView }>(
        `/orgs/${enc(orgId)}/connectors/jira/draft/${enc(draftId)}`,
      )
    ).draft
  }

  async chooseJiraSite(orgId: string, draftId: string, siteId: string): Promise<ConnectionView> {
    return (
      await this.http.post<{ connection: ConnectionView }>(
        `/orgs/${enc(orgId)}/connectors/jira/draft/${enc(draftId)}`,
        { siteId },
      )
    ).connection
  }

  /** Read the mapped boards again now. */
  async syncJira(orgId: string): Promise<ConnectionView> {
    return (
      await this.http.post<{ connection: ConnectionView }>(`/orgs/${enc(orgId)}/connectors/jira/sync`)
    ).connection
  }

  async disconnectJira(orgId: string): Promise<void> {
    await this.http.delete(`/orgs/${enc(orgId)}/connectors/jira`)
  }

  /** The live Jira projects, for the import: which are teams already, which were removed (admins). */
  async jiraProjects(orgId: string): Promise<JiraProjectInfo[]> {
    return (
      await this.http.get<{ projects: JiraProjectInfo[] }>(`/orgs/${enc(orgId)}/connectors/jira/projects`)
    ).projects
  }

  /** Make teams of the chosen projects (admins). Their people and tickets are read in the background. */
  async importJiraProjects(orgId: string, input: ImportProjectsInput): Promise<ImportProjectsResult> {
    return (
      await this.http.post<{ result: ImportProjectsResult }>(
        `/orgs/${enc(orgId)}/connectors/jira/import`,
        input,
      )
    ).result
  }

  /** Whether new Jira projects become teams on their own (admins). */
  async setJiraAutoImport(orgId: string, autoImport: boolean): Promise<ConnectionView> {
    return (
      await this.http.put<{ connection: ConnectionView }>(
        `/orgs/${enc(orgId)}/connectors/jira/settings`,
        { autoImport },
      )
    ).connection
  }

  /** What has been read of a team's Jira project: its boards' sprints and tickets, as last read. */
  async teamBoard(orgId: string, teamId: string): Promise<TeamBoardView> {
    return (
      await this.http.get<{ board: TeamBoardView }>(`/orgs/${enc(orgId)}/teams/${enc(teamId)}/board`)
    ).board
  }

  /**
   * What the organisation's recent meetings left behind (the last `days`, 14 by default): what changed on your own
   * tickets, who is waiting on you, what was suggested for your tickets and never decided; and, for a team you lead
   * (or any team, for an admin), that team's changes. A meeting with nothing in it for you is left out.
   */
  async digests(orgId: string, days?: number): Promise<MeetingDigest[]> {
    const q = days ? `?days=${days}` : ''
    return (await this.http.get<{ digests: MeetingDigest[] }>(`/orgs/${enc(orgId)}/digests${q}`)).digests
  }

  /**
   * What a team's board has to say before its meeting: blocked, late, due soon, in progress for a while and
   * unassigned. For the team's project manager or an admin, and for no one else.
   */
  async teamBrief(orgId: string, teamId: string): Promise<BriefView> {
    return (await this.http.get<{ brief: BriefView }>(`/orgs/${enc(orgId)}/teams/${enc(teamId)}/brief`)).brief
  }

  // ------------------------------------------------------------------ a team's roster of Jira people

  /** The team's Jira people: who has joined, who is flagged, who is probably who (admins see emails). */
  async teamRoster(orgId: string, teamId: string): Promise<TeamRosterView> {
    return (
      await this.http.get<{ roster: TeamRosterView }>(`${this.rosterPath(orgId, teamId)}`)
    ).roster
  }

  /** Add the address to invite a person at, or keep (or stop keeping) someone who is flagged. */
  async updateRosterPerson(
    orgId: string,
    teamId: string,
    personId: string,
    input: { contactEmail?: string | null; keep?: boolean },
  ): Promise<void> {
    await this.http.patch(`${this.rosterPath(orgId, teamId)}/${enc(personId)}`, input)
  }

  /** Off the roster and the team, and not added again. */
  async removeRosterPerson(orgId: string, teamId: string, personId: string): Promise<void> {
    await this.http.delete(`${this.rosterPath(orgId, teamId)}/${enc(personId)}`)
  }

  /** Say which of our members a Jira person is. */
  async linkRosterPerson(
    orgId: string,
    teamId: string,
    personId: string,
    userId: string,
  ): Promise<void> {
    await this.http.put(`${this.rosterPath(orgId, teamId)}/${enc(personId)}/member`, { userId })
  }

  async unlinkRosterPerson(orgId: string, teamId: string, personId: string): Promise<void> {
    await this.http.delete(`${this.rosterPath(orgId, teamId)}/${enc(personId)}/member`)
  }

  /** Make a joined person the team's project manager. */
  async makeProjectManager(orgId: string, teamId: string, personId: string): Promise<void> {
    await this.http.post(`${this.rosterPath(orgId, teamId)}/${enc(personId)}/manager`)
  }

  /** An invite link made for one Jira person: accepting it links them and puts them on their teams. */
  async inviteRosterPerson(orgId: string, teamId: string, personId: string): Promise<CreatedInvite> {
    return this.http.post(`${this.rosterPath(orgId, teamId)}/${enc(personId)}/invite`)
  }

  /**
   * Email one person their invitation. When the email cannot go out (mail is off, the day's limit is
   * reached, the service failed) the result carries the link to copy instead.
   */
  async sendRosterInvite(orgId: string, teamId: string, personId: string): Promise<InviteSendResult> {
    return this.http.post(`${this.rosterPath(orgId, teamId)}/${enc(personId)}/send`)
  }

  /** Email everyone on the roster who has an address and has not joined or been invited. */
  async inviteEveryone(orgId: string, teamId: string): Promise<SendAllResult> {
    return this.http.post(`/orgs/${enc(orgId)}/teams/${enc(teamId)}/roster-invite-all`)
  }

  /** Save addresses for people on the roster, matched by their Jira email or name. */
  async setRosterEmails(
    orgId: string,
    teamId: string,
    entries: Array<{ name?: string | null; email: string }>,
  ): Promise<SetEmailsResult> {
    return this.http.put(`/orgs/${enc(orgId)}/teams/${enc(teamId)}/roster-emails`, { entries })
  }

  // ------------------------------------------------------------------ scheduled meetings

  /** The meetings you can see that happen between two moments (ms), and each occurrence. At most 100 days. */
  async schedule(orgId: string, range: { from: number; to: number; teamId?: string }): Promise<ScheduleView> {
    const q = new URLSearchParams({ from: String(range.from), to: String(range.to) })
    if (range.teamId) q.set('teamId', range.teamId)
    return this.http.get(`/orgs/${enc(orgId)}/schedule?${q}`)
  }

  /** Turn your own reminder emails (shortly before your teams' meetings) on or off. */
  async setMeetingReminders(orgId: string, on: boolean): Promise<void> {
    await this.http.put(`/orgs/${enc(orgId)}/reminders`, { on })
  }

  /** An admin or the team's project manager schedules a meeting; its room is made with it. */
  async scheduleMeeting(orgId: string, teamId: string, input: MeetingInput): Promise<MeetingView> {
    return (
      await this.http.post<{ meeting: MeetingView }>(`/orgs/${enc(orgId)}/teams/${enc(teamId)}/meetings`, input)
    ).meeting
  }

  /** Change the whole series. */
  async updateMeeting(orgId: string, meetingId: string, patch: Partial<MeetingInput>): Promise<MeetingView> {
    return (
      await this.http.patch<{ meeting: MeetingView }>(`/orgs/${enc(orgId)}/meetings/${enc(meetingId)}`, patch)
    ).meeting
  }

  /** Cancel the whole series. */
  async cancelMeeting(orgId: string, meetingId: string): Promise<void> {
    await this.http.delete(`/orgs/${enc(orgId)}/meetings/${enc(meetingId)}`)
  }

  /** Cancel one day of a repeating meeting (`YYYY-MM-DD`, its own time zone), or bring it back. */
  async skipMeetingDay(orgId: string, meetingId: string, date: string, skipped = true): Promise<void> {
    const base = `/orgs/${enc(orgId)}/meetings/${enc(meetingId)}/skips`
    if (skipped) await this.http.put(base, { date })
    else await this.http.delete(`${base}/${enc(date)}`)
  }

  /** The coming meetings as an iCalendar file (text), for the person's calendar app. */
  async calendarFile(orgId: string, teamId?: string): Promise<{ name: string; ics: string }> {
    const q = teamId ? `?teamId=${enc(teamId)}` : ''
    return this.http.get(`/orgs/${enc(orgId)}/schedule/calendar${q}`)
  }

  private rosterPath(orgId: string, teamId: string): string {
    return `/orgs/${enc(orgId)}/teams/${enc(teamId)}/roster`
  }

  /**
   * Hear about an organisation's changes as they happen (a read of Jira finished, someone joined, a
   * team was made), until the returned function is called. A change says only what kind of thing
   * moved and which team; ask for what you need. Nothing is polled.
   */
  watch(orgId: string, listener: OrgListener): () => void {
    return this.events.watch(orgId, listener)
  }

  /** The organisation this browser last showed, so a person in several lands where they left off. */
  current(): string | null {
    return readKey(this.storage, CURRENT_KEY)
  }

  remember(orgId: string | null): void {
    writeKey(this.storage, CURRENT_KEY, orgId)
  }
}
